/**
 * The record a tile shows, for the assistant and voice button of the page
 * around it (I-3641). The buttons live once in the page; the surface that
 * claims the record (B-DMS) lives in the tile's own Connect, with its own
 * `assistantChannel`. So the page asks the active tile over the tile protocol
 * (`connect.tile.assistant-context`, `tile.ts`) and the tile answers from its
 * channel. Both sides check origin and sender, and an unanswered question
 * (a tile that is still loading or never answers) is simply no context.
 */
import type {
  AgentRecordContext,
  ConnectTileAssistantContextReply,
  ConnectTileCommand,
} from "@blpde/connect";
import { connectTileId } from "@blpde/connect/config";
import { useTileWorkspaces } from "./useTileWorkspaces";

/** Long enough for a tile that is awake, short enough that the button never feels stuck. */
export const TILE_CONTEXT_TIMEOUT_MS = 300;

/** A claim only counts with module, type and id; the label is optional. */
export function recordContextOf(
  context: Partial<AgentRecordContext> | null | undefined,
): AgentRecordContext | null {
  const text = (value: unknown): string =>
    typeof value === "string" ? value.trim() : "";
  const moduleKey = text(context?.moduleKey);
  const resourceType = text(context?.resourceType);
  const resourceId = text(context?.resourceId);
  if (!moduleKey || !resourceType || !resourceId) return null;
  return {
    moduleKey,
    resourceType,
    resourceId,
    label: text(context?.label) || undefined,
  };
}

/** The page: ask one tile's frame; null on no answer within the timeout. */
export function requestTileAssistantContext(
  tileId: string,
  frame: Window | null | undefined,
  timeoutMs = TILE_CONTEXT_TIMEOUT_MS,
): Promise<AgentRecordContext | null> {
  if (!frame) return Promise.resolve(null);
  return new Promise((resolve) => {
    const requestId = crypto.randomUUID();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const finish = (context: AgentRecordContext | null): void => {
      clearTimeout(timer);
      window.removeEventListener("message", onMessage);
      resolve(context);
    };
    function onMessage(
      event: MessageEvent<ConnectTileAssistantContextReply>,
    ): void {
      if (event.origin !== location.origin || event.source !== frame) return;
      const reply = event.data;
      if (
        reply?.type !== "connect.tile.assistant-context.reply" ||
        reply.requestId !== requestId ||
        reply.tileId !== tileId
      )
        return;
      finish(recordContextOf(reply.context));
    }
    window.addEventListener("message", onMessage);
    timer = setTimeout(() => finish(null), timeoutMs);
    const command: ConnectTileCommand = {
      type: "connect.tile.assistant-context",
      requestId,
    };
    frame.postMessage(command, location.origin);
  });
}

/** The page: the record of the tile that has the focus in the active workspace. */
export function requestActiveTileContext(
  timeoutMs = TILE_CONTEXT_TIMEOUT_MS,
): Promise<AgentRecordContext | null> {
  const tileId = useTileWorkspaces().active.value?.activeTileId;
  if (!tileId) return Promise.resolve(null);
  const pane = [
    ...document.querySelectorAll<HTMLElement>("[data-tile-id]"),
  ].find((el) => el.dataset.tileId === tileId);
  const frame = pane?.querySelector("iframe")?.contentWindow;
  return requestTileAssistantContext(tileId, frame, timeoutMs);
}

/** A tile: answer the page's question from the tile's own claim. Returns the way to stop. */
export function answerTileAssistantContext(
  read: () => Partial<AgentRecordContext> | null | undefined,
): () => void {
  const tileId = connectTileId();
  if (!tileId || window.parent === window) return () => {};
  const readSafely = () => {
    try {
      return read();
    } catch {
      return null; // a claimant that fails is no context, not no answer
    }
  };
  function onMessage(event: MessageEvent<ConnectTileCommand>): void {
    if (event.source !== window.parent || event.origin !== location.origin)
      return;
    if (event.data?.type !== "connect.tile.assistant-context") return;
    const reply: ConnectTileAssistantContextReply = {
      type: "connect.tile.assistant-context.reply",
      tileId: tileId!,
      requestId: event.data.requestId,
      context: recordContextOf(readSafely()),
    };
    window.parent.postMessage(reply, location.origin);
  }
  window.addEventListener("message", onMessage);
  return () => window.removeEventListener("message", onMessage);
}
